import { useSession, signOut } from 'next-auth/react'
import React, { useState, useEffect, useCallback, useRef } from 'react'

const LOGOUT_TIMEOUT_MS = 1800000 // 30 minutes
const LOGOUT_TIMEOUT_CHECK_MS = 10000 // less 1 minute
const STORAGE_KEY = '_lastActivity'

type AutoLogoutProviderProps = {
  timeoutMs?: number
  timeoutCheckMs?: number
  children?: React.ReactNode
}

const getStoredLastActivity = (): number => {
  try {
    const stored = globalThis.localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsedValue = Number.parseInt(stored, 10)
      if (!Number.isNaN(parsedValue)) {
        return parsedValue
      }
    }
  } catch {
    return Date.now()
  }
  return Date.now()
}

export const AutoLogoutProvider: React.FC<AutoLogoutProviderProps> = ({
  timeoutMs = LOGOUT_TIMEOUT_MS,
  timeoutCheckMs = LOGOUT_TIMEOUT_CHECK_MS,
  children,
}) => {
  const [lastActivity, setLastActivity] = useState<number>(() => getStoredLastActivity())
  const { status } = useSession()
  const hasCheckedInitialInactivity = useRef(false)

  const storage = () => globalThis.localStorage

  const onUserActivity = useCallback(() => {
    if (status === 'authenticated') {
      const now = Date.now()
      storage().setItem(STORAGE_KEY, now.toString())
      setLastActivity(now)
    }
  }, [status])

  const isUserInactive = useCallback(() => {
    const now = Date.now()
    if (now - lastActivity > timeoutMs && status === 'authenticated') {
      storage().removeItem(STORAGE_KEY)
      void signOut()
      return true
    }
    return false
  }, [lastActivity, timeoutMs, status])

  const onTimerElapsed = useCallback(() => {
    isUserInactive()
  }, [isUserInactive])

  const onStorage = useCallback(
    (event: StorageEvent) => {
      if (event.key === STORAGE_KEY && status === 'authenticated') {
        const newValue = Number.parseInt(event.newValue ?? '0')
        setLastActivity(newValue)
      }
    },
    [status],
  )

  useEffect(() => {
    if (status === 'authenticated' && !hasCheckedInitialInactivity.current) {
      hasCheckedInitialInactivity.current = true
      const now = Date.now()
      storage().setItem(STORAGE_KEY, now.toString())
      setLastActivity(now)
    }
  }, [status])

  useEffect(() => {
    const intervalId = setInterval(onTimerElapsed, timeoutCheckMs)
    const additionalWindowEvents = ['mousemove', 'click', 'keydown', 'scroll']

    additionalWindowEvents.forEach((eventName) => {
      globalThis.addEventListener(eventName, onUserActivity)
    })

    globalThis.addEventListener('storage', onStorage)

    return () => {
      clearInterval(intervalId)
      additionalWindowEvents.forEach((eventName) => {
        globalThis.removeEventListener(eventName, onUserActivity)
      })
      globalThis.removeEventListener('storage', onStorage)
    }
  }, [onUserActivity, onStorage, onTimerElapsed, timeoutCheckMs])

  return <>{children}</>
}
